import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useManagerTheme } from '../context/ManagerThemeContext';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerMobileSidebar from '../components/ManagerMobileSidebar';
import ManagerHeader from '../components/ManagerHeader';
import Footer from '../components/Footer';
import api from '../services/api';
import LogoutModal from '../components/LogoutModal';
import DarkSelect from '../components/DarkSelect';
import { scheduleLoadingEnd } from '../utils/loadingDelay';

interface ReportData {
  walletBalance: number;
  totalMerchantsBalance: number;
  sparkupCredit: number;
  todayCredits: number;
  todayDebits: number;
  todayCharges: number;
  totalCredits: number;
  totalUsers: number;
  activeUsers: number;
  totalPayouts: number;
  pendingPayouts: number;
  completedPayouts: number;
  totalTransactions: number;
  totalCreditTransactions: number;
  totalDebitTransactions: number;
}

interface Transaction {
  id: number;
  user_id: number;
  transaction_type: string;
  amount: number;
  balance_before?: number;
  balance_after: number;
  description: string;
  created_at: string;
  user_name: string;
  user_email: string;
  user_role: string;
  status?: string;
}

interface ReportFilters {
  fromDate: string;
  toDate: string;
  paymentType: string;
  status: string;
  amount: string;
  userName: string;
  userEmail: string;
}

const ManagerReports: React.FC = () => {
  const { user, logout } = useAuth();
  const { sidebarColor, contentColor, sectionColor, tableColor } = useManagerTheme();
  const [reportData, setReportData] = useState<ReportData>({
    walletBalance: 0,
    totalMerchantsBalance: 0,
    sparkupCredit: 0,
    todayCredits: 0,
    todayDebits: 0,
    todayCharges: 0,
    totalCredits: 0,
    totalUsers: 0,
    activeUsers: 0,
    totalPayouts: 0,
    pendingPayouts: 0,
    completedPayouts: 0,
    totalTransactions: 0,
    totalCreditTransactions: 0,
    totalDebitTransactions: 0
  });
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reports');
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [filters, setFilters] = useState<ReportFilters>({
    fromDate: '',
    toDate: '',
    paymentType: '',
    status: '',
    amount: '',
    userName: '',
    userEmail: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage] = useState(50);
  const loadingStartRef = useRef(0);

  useEffect(() => {
    fetchReportsData();
  }, [currentPage]);

  const fetchReportsData = async () => {
    try {
      loadingStartRef.current = Date.now();
      setLoading(true);
      
      // Dashboard stats will be calculated from transactions data

      // Fetch transactions
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('date_from', filters.fromDate);
      if (filters.toDate) params.append('date_to', filters.toDate);
      if (filters.paymentType) params.append('transaction_type', filters.paymentType);
      if (filters.status) params.append('status', filters.status);
      if (filters.amount) params.append('amount', filters.amount);
      if (filters.userName) params.append('user_name', filters.userName);
      if (filters.userEmail) params.append('user_email', filters.userEmail);
      params.append('page', currentPage.toString());
      params.append('limit', recordsPerPage.toString());
      
      console.log('📤 Frontend sending filters:', filters);
      console.log('📤 URL params:', params.toString());
      
      const transactionsResponse = await api.get(`/dashboard/manager/transactions?${params.toString()}`);
      if (transactionsResponse.data.success) {
        const allTransactions = transactionsResponse.data.data.transactions || [];
        setTransactions(allTransactions);
        setTotalPages(transactionsResponse.data.data.pagination.totalPages || 1);
        setTotalRecords(transactionsResponse.data.data.pagination.totalRecords || 0);
        
        // Calculate totals for current merchant from transactions
        const creditTransactions = allTransactions.filter((t: Transaction) => 
          t.transaction_type && t.transaction_type.toLowerCase().includes('credit')
        );
        const debitTransactions = allTransactions.filter((t: Transaction) => 
          t.transaction_type && t.transaction_type.toLowerCase().includes('debit')
        );
        
        // Calculate total amounts for current merchant with proper data validation
        const totalCredits = creditTransactions.reduce((sum: number, t: Transaction) => {
          const amount = typeof t.amount === 'number' ? t.amount : 
                        typeof t.amount === 'string' ? parseFloat(t.amount) || 0 : 0;
          console.log('Credit transaction amount:', t.amount, 'Parsed:', amount);
          return sum + amount;
        }, 0);
        
        const totalDebits = debitTransactions.reduce((sum: number, t: Transaction) => {
          const amount = typeof t.amount === 'number' ? t.amount : 
                        typeof t.amount === 'string' ? parseFloat(t.amount) || 0 : 0;
          console.log('Debit transaction amount:', t.amount, 'Parsed:', amount);
          return sum + amount;
        }, 0);
        
        console.log('Calculated totals - Credits:', totalCredits, 'Debits:', totalDebits);
        
        // Ensure we never set NaN values
        const safeTotalCredits = isNaN(totalCredits) ? 0 : totalCredits;
        const safeTotalDebits = isNaN(totalDebits) ? 0 : totalDebits;
        
        // Update reportData with current merchant totals
        setReportData(prev => ({
          ...prev,
          walletBalance: safeTotalDebits, // My Total Debits for current merchant
          totalMerchantsBalance: safeTotalCredits, // My Total Credits for current merchant
          totalTransactions: transactionsResponse.data.data.pagination.totalRecords || 0,
          totalCreditTransactions: creditTransactions.length,
          totalDebitTransactions: debitTransactions.length
        }));
      }
    } catch (error) {
      console.error('Error fetching reports data:', error);
    } finally {
      scheduleLoadingEnd(loadingStartRef.current, setLoading);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    }).toLowerCase();
    return `${day} ${month} ${year}, ${time}`;
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    logout();
    setShowLogoutModal(false);
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const handleNavigation = (tabId: string) => {
    setActiveTab(tabId);
    switch (tabId) {
      case 'dashboard':
        window.location.href = '/manager';
        break;
      case 'account':
        window.location.href = '/manager/account';
        break;
      case 'payouts':
        window.location.href = '/manager/payouts';
        break;
      case 'reports':
        window.location.href = '/manager/reports';
        break;
      case 'merchants':
        window.location.href = '/manager/merchants';
        break;
      case 'escrow':
        window.location.href = '/manager/escrow';
        break;
      case 'credit':
        window.location.href = '/manager/merchant-report-history';
        break;
      default:
        break;
    }
  };

  const handleCreditMoney = () => {
    // TODO: Implement credit money functionality
    console.log('Credit money clicked');
  };

  const handleMyProfile = () => {
    window.location.href = '/manager/account';
  };

  const handleFilterChange = (key: keyof ReportFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchReportsData();
  };

  const handleClearFilters = () => {
    setFilters({
      fromDate: '',
      toDate: '',
      paymentType: '',
      status: '',
      amount: '',
      userName: '',
      userEmail: ''
    });
    setCurrentPage(1);
    fetchReportsData();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };


  const getTransactionStatusColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'credit':
        return '#10b981';
      case 'debit':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getTransactionStatusBg = (type: string) => {
    switch (type.toLowerCase()) {
      case 'credit':
        return 'rgba(16, 185, 129, 0.2)';
      case 'debit':
        return 'rgba(239, 68, 68, 0.2)';
      default:
        return 'rgba(148, 163, 184, 0.2)';
    }
  };

  const getStatusPillStyle = (status: string) => {
    const s = (status || '').toLowerCase();
    if (s === 'completed' || s === 'success') return { bg: 'bg-emerald-500/20', text: 'text-emerald-400' };
    if (s === 'failed' || s === 'error') return { bg: 'bg-red-500/20', text: 'text-red-400' };
    return { bg: 'bg-slate-500/20', text: 'text-slate-400' };
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
        <ManagerHeader
          user={user}
          onRefresh={fetchReportsData}
          onCreditMoney={handleCreditMoney}
          onMyProfile={handleMyProfile}
          onLogout={handleLogoutClick}
        />
        <div className="flex flex-1">
          <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}><ManagerSidebar activeTab={activeTab} onNavigation={handleNavigation} /></div>
          <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-8 backdrop-blur-sm text-slate-200" style={{ backgroundColor: contentColor }}>
            <div className="w-12 h-12 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin mb-5" />
            <p className="text-slate-200 text-base m-0">Loading reports...</p>
          </div>
        </div>
        {showLogoutModal && (
          <LogoutModal
            isOpen={showLogoutModal}
            onConfirm={handleLogoutConfirm}
            onClose={handleLogoutCancel}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
      <ManagerMobileSidebar
        showMobileSidebar={showMobileSidebar}
        setShowMobileSidebar={setShowMobileSidebar}
        activeTab="reports"
        onCreditMoney={handleCreditMoney}
        onLogout={handleLogoutClick}
      />
      <ManagerHeader
        user={user}
        onRefresh={fetchReportsData}
        onCreditMoney={handleCreditMoney}
        onMyProfile={handleMyProfile}
        onLogout={handleLogoutClick}
      />
      <div className="flex flex-1">
        <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}><ManagerSidebar activeTab={activeTab} onNavigation={handleNavigation} /></div>
        <div className="flex-1 flex flex-col text-slate-200 p-6 md:p-8" style={{ backgroundColor: contentColor }}>
          <div className="flex-shrink-0 min-w-[300px] mb-4">
            <h1 className="text-slate-100 text-2xl font-bold m-0">Manager Report (Current)</h1>
            <h3 className="text-slate-300 text-lg font-medium mt-1 m-0">Manager CREDIT and DEBIT Reports</h3>
          </div>
          <div className="rounded-2xl p-8 mb-8 border border-white/[0.06] shadow-xl shadow-black/25" style={{ backgroundColor: sectionColor }}>
            <div className="flex items-center justify-start gap-4 flex-wrap">
              <div className="flex gap-4 flex-wrap justify-start">
                <div className="flex items-center gap-5 min-w-[240px] min-h-[100px] rounded-xl p-6 bg-gradient-to-br from-red-900 to-red-950 border-2 border-red-500 shadow-lg">
                  <div className="w-14 h-14 rounded-lg flex items-center justify-center text-3xl flex-shrink-0 shadow-lg bg-gradient-to-br from-red-600 to-red-800 border-2 border-white/20 text-white">💰</div>
                  <div className="flex-1">
                    <h3 className="text-base font-black text-slate-300 uppercase tracking-wider mb-1.5 m-0">
                      My Total Debits
                      <span className="ml-1 text-sm font-black text-red-200 normal-case">
                        - {formatCurrency(reportData.walletBalance)}
                      </span>
                    </h3>
                    <p className="text-sm text-slate-400 mt-1 font-semibold m-0">Across my merchants</p>
                  </div>
                </div>
                <div className="flex items-center gap-5 min-w-[240px] min-h-[100px] rounded-xl p-6 bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-blue-500 shadow-lg">
                  <div className="w-14 h-14 rounded-lg flex items-center justify-center text-3xl flex-shrink-0 shadow-lg bg-gradient-to-br from-green-500 to-green-700 border-2 border-white/20 text-white">🏪</div>
                  <div className="flex-1">
                    <h3 className="text-base font-black text-slate-300 uppercase tracking-wider mb-1.5 m-0">
                      My Total Credits
                      <span className="ml-1 text-sm font-black text-slate-50 normal-case">
                        - {formatCurrency(reportData.totalMerchantsBalance)}
                      </span>
                    </h3>
                    <p className="text-sm text-slate-400 mt-1 font-semibold m-0">Towards my merchants</p>
                  </div>
                </div>
                <div className="flex items-center gap-5 min-w-[240px] min-h-[100px] rounded-xl p-6 bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-blue-500 shadow-lg">
                  <div className="w-14 h-14 rounded-lg flex items-center justify-center text-3xl flex-shrink-0 shadow-lg bg-gradient-to-br from-amber-500 to-amber-700 border-2 border-white/20 text-white">📊</div>
                  <div className="flex-1">
                    <h3 className="text-base font-black text-slate-300 uppercase tracking-wider mb-1.5 m-0">
                      Total Transactions
                      <span className="ml-1 text-sm font-black text-slate-50 normal-case">
                        - <span className="text-emerald-500">{reportData.totalCreditTransactions || 0} CREDIT</span>
                        <span className="mx-1">|</span>
                        <span className="text-red-500">{reportData.totalDebitTransactions || 0} DEBIT</span>
                      </span>
                    </h3>
                    <p className="text-sm text-slate-400 mt-1 font-semibold m-0">Transaction breakdown</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-6 mb-6 border border-white/[0.06] shadow-xl shadow-black/25" style={{ backgroundColor: sectionColor }}>
            <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-white text-xl font-bold m-0">
                Filter Transactions{' '}
                <span className="font-normal text-white/80 text-base">
                  – Use the filters below to find specific transaction records
                </span>
              </h3>
              <div className="mt-2 flex items-center gap-3 sm:mt-0">
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-700 py-2.5 px-5 text-sm font-semibold text-white shadow-lg border-none cursor-pointer"
                >
                  🔍 Apply Filters
                </button>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 py-2.5 px-5 text-sm font-semibold text-white shadow-lg border-none cursor-pointer"
                >
                  🔄 Clear Filters
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-8 items-start">
              <div className="w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label htmlFor="fromDate" className="block text-sm font-semibold text-slate-300 mb-1.5">From Date</label>
                    <div className="relative">
                      <input id="fromDate" type="date" value={filters.fromDate} onChange={(e) => handleFilterChange('fromDate', e.target.value)} className="w-full py-3 pl-4 pr-11 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-11 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-300" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      </span>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="toDate" className="block text-sm font-semibold text-slate-300 mb-1.5">To Date</label>
                    <div className="relative">
                      <input id="toDate" type="date" value={filters.toDate} onChange={(e) => handleFilterChange('toDate', e.target.value)} className="w-full py-3 pl-4 pr-11 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-11 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-300" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      </span>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="paymentType" className="block text-sm font-semibold text-slate-300 mb-1.5">Transaction Type</label>
                    <DarkSelect
                      id="paymentType"
                      value={filters.paymentType}
                      onChange={(v) => handleFilterChange('paymentType', v)}
                      placeholder="All Types"
                      options={[
                        { value: '', label: 'All Types' },
                        { value: 'credit', label: 'Credit' },
                        { value: 'debit', label: 'Debit' },
                      ]}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="amount" className="block text-sm font-semibold text-slate-300 mb-1.5">Amount</label>
                    <input id="amount" type="number" placeholder="Amount" value={filters.amount} onChange={(e) => handleFilterChange('amount', e.target.value)} step="0.01" className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label htmlFor="userName" className="block text-sm font-semibold text-slate-300 mb-1.5">User Name</label>
                    <input id="userName" type="text" placeholder="User name" value={filters.userName} onChange={(e) => handleFilterChange('userName', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                  </div>
                  <div>
                    <label htmlFor="userEmail" className="block text-sm font-semibold text-slate-300 mb-1.5">Email Address</label>
                    <input id="userEmail" type="email" placeholder="Email address" value={filters.userEmail} onChange={(e) => handleFilterChange('userEmail', e.target.value)} className="w-full py-3 px-4 border-2 border-white/[0.12] rounded-lg text-slate-200 bg-slate-800/80 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20" />
                  </div>
                  <div>
                    <label htmlFor="status" className="block text-sm font-semibold text-slate-300 mb-1.5">Status</label>
                    <DarkSelect
                      id="status"
                      value={filters.status}
                      onChange={(v) => handleFilterChange('status', v)}
                      placeholder="All Status"
                      options={[
                        { value: '', label: 'All Status' },
                        { value: 'completed', label: 'Completed' },
                        { value: 'failed', label: 'Failed' },
                      ]}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-slate-100 text-xl font-bold m-0">Transaction History</h3>
              <span className="h-px flex-1 max-w-[120px] bg-gradient-to-r from-white/20 to-transparent rounded-full" aria-hidden="true" />
            </div>
            {totalPages > 1 && (
              <div className="flex flex-wrap justify-between items-center gap-4 py-4 px-5 mb-4 bg-slate-800/60 rounded-xl border border-white/[0.08]">
                <div className="text-sm text-slate-400 font-medium">
                  Showing {((currentPage - 1) * recordsPerPage) + 1} to {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} transactions
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="py-2 px-4 border border-white/[0.12] bg-slate-800/80 text-slate-200 rounded-lg text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                    Previous
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;
                      return (
                        <button key={pageNum} type="button" onClick={() => handlePageChange(pageNum)} className={`w-10 h-10 flex items-center justify-center rounded-lg text-sm font-medium cursor-pointer ${currentPage === pageNum ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white border-blue-500' : 'border border-white/[0.12] bg-slate-800/80 text-slate-200'}`}>
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button type="button" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="py-2 px-4 border border-white/[0.12] bg-slate-800/80 text-slate-200 rounded-lg text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                    Next
                  </button>
                </div>
              </div>
            )}

            {transactions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 bg-white/[0.06] rounded-2xl border border-white/[0.08]">
                <div className="text-5xl mb-4">📋</div>
                <h3 className="text-xl font-bold text-slate-100 mb-2 m-0">No transactions found</h3>
                <p className="text-slate-300 m-0">No transactions match your current filters.</p>
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl shadow-black/20" style={{ backgroundColor: tableColor }}>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px] border-collapse text-sm">
                    <thead>
                      <tr className="bg-slate-700/60">
                        <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Date</th>
                        <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">To Merchant</th>
                        <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Balance Before</th>
                        <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Amount</th>
                        <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Balance After</th>
                        <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Transaction Type</th>
                        <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Description</th>
                        <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Status</th>
                        <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Merchant Email</th>
                        <th className="text-slate-200 font-semibold py-3.5 px-4 text-left border-b border-white/[0.1] text-xs uppercase tracking-widest">Role</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((transaction, index) => {
                        const statusStyle = getStatusPillStyle(transaction.status || '');
                        return (
                          <tr key={transaction.id} className={`border-b border-white/[0.06] transition-colors hover:bg-white/[0.06] ${index % 2 === 1 ? 'bg-white/[0.02]' : ''}`}>
                            <td className="py-3.5 px-4 text-slate-300 align-middle whitespace-nowrap">{formatDate(transaction.created_at)}</td>
                            <td className="py-3.5 px-4 text-slate-200 font-medium align-middle">{transaction.user_name}</td>
                            <td className="py-3.5 px-4 text-slate-300 align-middle">{formatCurrency(transaction.balance_before || 0)}</td>
                            <td className="py-3.5 px-4 align-middle">
                              <span className={`font-semibold ${transaction.transaction_type && transaction.transaction_type.toLowerCase().includes('credit') ? 'text-emerald-400' : 'text-red-400'}`}>
                                {formatCurrency(transaction.amount)}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-slate-300 align-middle">{formatCurrency(transaction.balance_after)}</td>
                            <td className="py-3.5 px-4 align-middle">
                              <span className="inline-block py-1.5 px-3 rounded-full text-xs font-semibold uppercase border border-white/10" style={{ backgroundColor: getTransactionStatusBg(transaction.transaction_type), color: getTransactionStatusColor(transaction.transaction_type) }}>
                                {transaction.transaction_type}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-slate-400 align-middle max-w-[200px] truncate">{transaction.description || '–'}</td>
                            <td className="py-3.5 px-4 align-middle">
                              <span className={`inline-block py-1.5 px-3 rounded-full text-xs font-semibold uppercase ${statusStyle.bg} ${statusStyle.text}`}>
                                {transaction.status || 'N/A'}
                              </span>
                            </td>
                            <td className="py-3.5 px-4 text-slate-300 align-middle text-[13px]">{transaction.user_email}</td>
                            <td className="py-3.5 px-4 align-middle">
                              <span className="inline-block py-1 px-2.5 rounded-md text-xs font-medium bg-white/10 text-slate-400 border border-white/5">{transaction.user_role}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showLogoutModal && (
        <LogoutModal
          isOpen={showLogoutModal}
          onConfirm={handleLogoutConfirm}
          onClose={handleLogoutCancel}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ManagerReports;